import {
  GET_DOCUMENT,
  getAllPendingProposals,
  resetDocData,
} from "app/redux/features/pending-proposals";
import { makeRequest } from "app/utils/makeRequest";
import { getSessionStore } from "app/utils/sessionStore";
import { CONTENT_TYPE, THEME, USERTYPE } from "app/utils/types";
import { getFileIconClass, getFileName } from "app/utils/utils";
import { Badge } from "primereact/badge";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ImFileZip } from "react-icons/im";
import { useDispatch, useSelector } from "react-redux";
import FinancingIcon from "../../assets/IconComponent/FinancingIcon";
import PendingIcon from "../../assets/IconComponent/PendingIcon";
import PlanIcon from "../../assets/IconComponent/PlanIocn";
import PolicyIcon from "../../assets/IconComponent/PolicyIcon";
import ProposalsIcon from "../../assets/IconComponent/ProposalsIcon";
import ActivityRecord from "../../assets/svg/activity-record.svg";
import ActivityCompletedRecord from "../../assets/svg/activity-completed.svg";
import BillingHistory from "../../assets/svg/billing-history.svg";
import NoteEdit from "../../assets/svg/note-edit.svg";
import ThreadCardBadge from "../../assets/svg/thread-card-badge.svg";
import PremiumFinanceThreadEditor from "./PremiumFinanceThreadsEditor";
import CalendarIcon from "app/assets/IconComponent/CalendarIcon";
import { CustomBadge } from "v2/common/CustomBadge";
import { getObjectFromLocalStore } from "app/utils/localStore";

// PrimeReact icons will be used via className

// TypeScript interfaces for API Response
interface UserProfile {
  firstName: string;
  lastName: string;
  profilePicture?: string;
}

interface User {
  id: string;
  email: string;
  profile: UserProfile | null;
}

interface Mention {
  userId: string;
  user: {
    email: string;
    profile: UserProfile | null;
  };
}

interface ThreadAttachment {
  id: string;
  threadId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  s3Key: string;
  createdAt: string;
}

interface Thread {
  id: string;
  content: string;
  contentType: string;
  threadType: string;
  userPersona: string;
  planId: string | null;
  proposalId: string;
  policyId: string | null;
  activityType: string | null;
  transactionId: string | null;
  createdById: string | null;
  updatedById: string | null;
  createdAt: string;
  updatedAt: string;
  mentions: Mention[];
  attachments: ThreadAttachment[];
  createdBy: User | null;
}

// Interface for display
interface Attachment {
  name: string;
  icon: string;
  s3Key: string;
}

interface ActivityUser {
  name: string;
  avatar: string | null;
}

interface TimelineActivity {
  id: string;
  dateGroup: string;
  type: CONTENT_TYPE;
  threadType?: string;
  user: ActivityUser;
  description: string;
  timestamp: string;
  status?:
    | "Completed"
    | "Pending"
    | "In Progress"
    | "Approved"
    | "Rejected"
    | "Deleted"
    | "Confirmed"
    | "Skipped"
    | "Viewed";
  attachment?: Attachment;
  attachments?: Attachment[];
}

const THREAD_TIME_ZONE = "America/Chicago";

const getDateKeyInTimeZone = (date: Date, timeZone: string): string => {
  const parts = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  }).formatToParts(date);

  const year = parts.find((p) => p.type === "year")?.value ?? "0000";
  const month = parts.find((p) => p.type === "month")?.value ?? "00";
  const day = parts.find((p) => p.type === "day")?.value ?? "00";

  return `${year}-${month}-${day}`;
};

const formatDateGroupInTimeZone = (date: Date, timeZone: string): string =>
  new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    timeZone,
  }).format(date);

// Helper function to get date group (Today, Yesterday, or formatted date)
const getDateGroup = (dateString: string): string => {
  const date = new Date(dateString);
  const todayKey = getDateKeyInTimeZone(new Date(), THREAD_TIME_ZONE);
  const yesterdayKey = getDateKeyInTimeZone(
    new Date(Date.now() - 24 * 60 * 60 * 1000),
    THREAD_TIME_ZONE,
  );
  const dateKey = getDateKeyInTimeZone(date, THREAD_TIME_ZONE);

  if (dateKey === todayKey) {
    return "Today";
  }
  if (dateKey === yesterdayKey) {
    return "Yesterday";
  }

  return formatDateGroupInTimeZone(date, THREAD_TIME_ZONE);
};

// Helper function to format timestamp (always shown in CST/US Central Time)
const formatTimestamp = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: THREAD_TIME_ZONE,
  });
};

// Enum for activity types
export enum PF_ACTIVITY_TYPE {
  DOCUMENT_REQUESTED = "document_requested",
  ACTION_REQUESTED = "action_requested",
  DOCUMENT_UPLOADED = "document_uploaded",
  ACTION_COMPLETED = "action_completed",
  INPUT_DETAILS_REQUESTED = "input_details_requested",
  INPUT_DETAILS_COMPLETED = "input_details_completed",
  DOCUMENT_REJECTED = "document_rejected",
  INPUT_DETAILS_REJECTED = "input_details_rejected",
  DOCUMENT_APPROVED = "document_approved",
  INPUT_DETAILS_APPROVED = "input_details_approved",
  SUB_STEP_COMPLETED = "sub_step_completed",
  STEP_COMPLETED = "step_completed",
  PLAN_CREATED = "plan_created",
  INVOICE_GENERATED = "invoice_generated",
  INVOICE_PAID = "invoice_paid",
  INVOICE_DELETED = "invoice_deleted",
  TASK_DELETED = "task_deleted",
}

// Helper function to map activityType to status
const getStatusFromActivityType = (
  activityType: string | null,
):
  | "Completed"
  | "Pending"
  | "Approved"
  | "Rejected"
  | "In Progress"
  | "Deleted"
  | "Confirmed"
  | "Skipped"
  | "Viewed"
  | undefined => {
  if (!activityType) return undefined;

  switch (activityType) {
    // Pending statuses
    case PF_ACTIVITY_TYPE.DOCUMENT_REQUESTED:
    case PF_ACTIVITY_TYPE.ACTION_REQUESTED:
    case PF_ACTIVITY_TYPE.INPUT_DETAILS_REQUESTED:
      return "Pending";

    // Completed statuses
    case PF_ACTIVITY_TYPE.DOCUMENT_UPLOADED:
    case PF_ACTIVITY_TYPE.ACTION_COMPLETED:
    case PF_ACTIVITY_TYPE.INPUT_DETAILS_COMPLETED:
    case PF_ACTIVITY_TYPE.SUB_STEP_COMPLETED:
    case PF_ACTIVITY_TYPE.STEP_COMPLETED:
    case PF_ACTIVITY_TYPE.PLAN_CREATED:
    case PF_ACTIVITY_TYPE.INVOICE_GENERATED:
    case PF_ACTIVITY_TYPE.INVOICE_PAID:
      return "Completed";

    // Rejected statuses
    case PF_ACTIVITY_TYPE.DOCUMENT_REJECTED:
    case PF_ACTIVITY_TYPE.INPUT_DETAILS_REJECTED:
      return "Rejected";

    // Approved statuses
    case PF_ACTIVITY_TYPE.DOCUMENT_APPROVED:
    case PF_ACTIVITY_TYPE.INPUT_DETAILS_APPROVED:
      return "Approved";

    // Deleted statuses
    case PF_ACTIVITY_TYPE.INVOICE_DELETED:
    case PF_ACTIVITY_TYPE.TASK_DELETED:
      return "Deleted";

    // Review statuses
    case "review_confirmed":
      return "Completed";
    case "review_skipped":
      return "Skipped";
    case "review_opened":
      return "Viewed";

    default:
      return undefined;
  }
};

const REVIEW_BADGE_VARIANTS = new Set(["confirmed", "skipped", "viewed", "completed", "deleted"]);

// Helper function to transform Thread to TimelineActivity
const transformThreadToActivity = (thread: Thread): TimelineActivity => {
  const userName = thread.createdBy?.profile?.firstName
    ? `${thread.createdBy.profile.firstName} ${thread.createdBy.profile.lastName}`
    : thread.createdBy?.email || "System";

  const avatar = thread.createdBy?.profile?.profilePicture || null;

  // Convert contentType to activity type
  const type = thread.contentType as CONTENT_TYPE;

  // Status is derived *only* from activityType; if there is no matching mapping,
  // we do not infer a status from threadType.
  const status = getStatusFromActivityType(thread.activityType);

  // Transform attachments
  const attachments: Attachment[] = thread.attachments.map((att) => ({
    name: att.fileName,
    icon: getFileIconClass(att.fileName),
    s3Key: att.s3Key,
  }));

  return {
    id: thread.id,
    dateGroup: getDateGroup(thread.createdAt),
    type,
    threadType: thread.threadType,
    user: {
      name: userName,
      avatar,
    },
    description: thread.content, // Keep HTML content as-is
    timestamp: formatTimestamp(thread.createdAt),
    status,
    attachments: attachments.length > 0 ? attachments : undefined,
  };
};

// Helper function to get timeline icon based on activity type
const getTimelineIcon = (type: CONTENT_TYPE, status: string = "") => {
  switch (type) {
    case CONTENT_TYPE.NOTES:
      return (
        <img src={NoteEdit} alt="NoteEdit" className="w-full h-full p-1 dark:filter dark:invert" />
      );
    case CONTENT_TYPE.ACTIVITY:
      if (status === "Completed") {
        return (
          <img
            src={ActivityCompletedRecord}
            alt="ActivityCompletedRecord"
            className="w-full h-full p-1 dark:filter dark:invert"
          />
        );
      }
      return (
        <img
          src={ActivityRecord}
          alt="ActivityRecord"
          className="w-full h-full p-1 dark:filter dark:invert"
        />
      );
    case CONTENT_TYPE.BILLING:
      return (
        <img
          src={BillingHistory}
          alt="BillingHistory"
          className="w-full h-full p-1 dark:filter dark:invert"
        />
      );
    default:
      return <i className="pi pi-file-o text-xl text-gray-600 text-sm dark:text-white" />;
  }
};

// Helper function to get status badge color
const getStatusBadgeColor = (status: string) => {
  switch (status) {
    case "Completed":
      return "success";
    case "Pending":
      return "warning";
    case "In Progress":
      return "info";
    case "Approved":
      return "success";
    case "Rejected":
      return "danger";
    case "Deleted":
      return "danger";
    default:
      return "danger";
  }
};

// Helper function to get thread type icon
const getThreadTypeIcon = (threadType: string | undefined, isDarkTheme: boolean) => {
  const iconColor = isDarkTheme ? "#ffffff" : "#00132E";
  const iconSize = "24px";

  if (!threadType) {
    return (
      <img
        src={ThreadCardBadge}
        alt="ThreadCardBadge"
        className="w-full h-full"
        style={{ WebkitTransform: "translateZ(0)", transform: "translateZ(0)" }}
      />
    );
  }

  switch (threadType.toUpperCase()) {
    case "PLANS":
      return <PlanIcon fill={iconColor} size={iconSize} />;
    case "FINANCING":
      return <FinancingIcon fill={iconColor} size={iconSize} />;
    case "PENDING":
      return <PendingIcon fill={iconColor} height={iconSize} width={iconSize} />;
    case "PROPOSAL":
      return <ProposalsIcon fill={iconColor} size={iconSize} />;
    case "POLICIES":
      return <PolicyIcon fill={iconColor} size={iconSize} />;
    case "REVIEW":
      return <CalendarIcon fill={iconColor} width={iconSize} height={iconSize} />;
    default:
      return (
        <img
          src={ThreadCardBadge}
          alt="ThreadCardBadge"
          className="w-full h-full"
          style={{ WebkitTransform: "translateZ(0)", transform: "translateZ(0)" }}
        />
      );
  }
};
// Activity Card Component
const ActivityCard: React.FC<{
  activity: TimelineActivity;
  isDarkTheme?: boolean;
  onOpenDoc: (s3Key: string) => void;
}> = ({ activity, isDarkTheme = false, onOpenDoc }) => {
  console.log("🚀 ~ ActivityCard ~ activity:", activity);
  return (
    <div
      className="relative flex items-start mb-4"
      style={{
        width: "100%",
        overflowX: "visible",
        overflowY: "visible",
        position: "relative",
      }}
    >
      {/* Timeline Icon */}
      <div
        className={`absolute ${
          isDarkTheme ? "bg-[#071526]" : "bg-white"
        }  flex items-center justify-center z-10`}
        style={
          {
            left: "-76px",
            top: "50%",
            width: "48px",
            height: "48px",
            minWidth: "48px",
            minHeight: "48px",
            WebkitTransform: "translate(-50%, -50%) translateZ(0)",
            transform: "translate(-50%, -50%) translateZ(0)",
            WebkitBackfaceVisibility: "hidden",
            backfaceVisibility: "hidden",
            position: "absolute",
            WebkitAppearance: "none",
          } as React.CSSProperties
        }
      >
        {getTimelineIcon(activity.type, activity.status)}
      </div>
      {/* Connector from icon to card */}
      <div
        className={`absolute ${
          isDarkTheme ? "border-white" : "border-gray-300"
        } border-t-2 border-dashed`}
        style={{
          left: "-50px",
          top: "50%",
          width: "calc(1rem + 55px)",
          WebkitBorderImage: "none",
          borderImage: "none",
          WebkitTransform: "translateY(-50%)",
          transform: "translateY(-50%)",
        }}
      />

      {/* Activity Card */}
      <div
        className="ml-4 w-full"
        style={{
          WebkitTransform: "translateZ(0)",
          transform: "translateZ(0)",
          maxWidth: "calc(100% - 1rem)",
          overflowX: "visible",
          overflowY: "visible",
          boxSizing: "border-box",
        }}
      >
        <div
          className="rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-[0_1px_3px_0_rgba(0,0,0,0.3)] hover:shadow-md dark:hover:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.4)] transition-shadow duration-200"
          style={{ WebkitBackfaceVisibility: "hidden", backfaceVisibility: "hidden" }}
        >
          {/* Top border accent */}

          <div className="p-4 pl-0">
            <div className="flex items-center" style={{ gap: "1rem" }}>
              {/* User Avatar */}
              <div className="flex-shrink-0" style={{ WebkitFlexShrink: "0", flexShrink: "0" }}>
                <div
                  className="w-[3rem] h-[3rem] flex items-center justify-center"
                  style={{
                    clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                  }}
                >
                  {activity.user.avatar ? (
                    <img
                      src={activity.user.avatar}
                      alt={activity.user.name}
                      className="w-full h-full object-cover"
                      style={{
                        WebkitTransform: "translateZ(0)",
                        transform: "translateZ(0)",
                      }}
                    />
                  ) : (
                    <div className="w-[3rem] h-[3rem] bg-blue-500 flex items-center justify-center">
                      <i className="pi pi-user text-white text-lg" />
                    </div>
                  )}
                </div>
              </div>

              {/* Activity Content */}
              <div
                className="flex-1 min-w-0 font-Inter"
                style={{ WebkitFlex: "1 1 0%", flex: "1 1 0%" }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1" style={{ WebkitFlex: "1 1 0%", flex: "1 1 0%" }}>
                    <h4
                      className={`text-sm font-semibold ${
                        isDarkTheme ? "text-white" : "text-gray-900"
                      } mb-1`}
                    >
                      {activity.user.name}
                    </h4>

                    <div className="flex items-center mb-2" style={{ gap: "0.75rem" }}>
                      {activity.status &&
                      REVIEW_BADGE_VARIANTS.has(activity.status.toLowerCase()) ? (
                        <div className="flex-shrink-0">
                          <CustomBadge
                            label={activity.status}
                            variant={activity.status.toLowerCase() as any}
                            className="normal-case"
                          />
                        </div>
                      ) : activity.status ? (
                        <Badge
                          value={activity.status}
                          severity={getStatusBadgeColor(activity.status)}
                          className="text-xs flex-shrink-0"
                          style={{ minWidth: "fit-content", marginTop: "2px" }}
                        />
                      ) : null}
                      <div
                        className="text-sm text-gray-700 leading-relaxed flex-1"
                        dangerouslySetInnerHTML={{ __html: activity.description }}
                      />
                    </div>

                    {activity.attachments && activity.attachments.length > 0 && (
                      <div className="flex flex-row flex-wrap gap-1 mb-2">
                        {activity.attachments.map((att, index) => {
                          const isZip = att.icon === "zip-icon";
                          return (
                            <div
                              key={index}
                              className="flex items-center ml-2 text-xs text-gray-500 cursor-pointer hover:text-blue-600 transition-colors"
                              onClick={() => onOpenDoc(att.s3Key)}
                            >
                              {isZip ? (
                                <ImFileZip className="mr-1 text-xs" />
                              ) : (
                                <i className={`${att.icon} mr-1`} />
                              )}
                              <span>{att.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <p
                      className={`text-xs  font-semibold  ${
                        isDarkTheme ? "text-white" : "text-[#6C757D]"
                      }`}
                    >
                      {activity.timestamp}
                    </p>
                  </div>

                  {/* Right-side Action Icon */}
                  <div
                    className="w-6 h-6 mt-4 rounded-lg flex  justify-end"
                    style={{ marginLeft: "1rem", WebkitFlexShrink: "0", flexShrink: "0" }}
                  >
                    {getThreadTypeIcon(activity.threadType, isDarkTheme)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Date Group Component
const DateGroup: React.FC<{
  dateGroup: string;
  activities: TimelineActivity[];
  isFirst?: boolean;
  isDarkTheme?: boolean;
  onOpenDoc: (s3Key: string) => void;
}> = ({ dateGroup, activities, isFirst = false, isDarkTheme = false, onOpenDoc }) => (
  <div
    className="mb-4"
    style={{ width: "100%", overflowX: "visible", overflowY: "visible", position: "relative" }}
  >
    {/* Date Header */}
    <div
      className="flex items-center justify-center mb-5"
      style={{
        marginLeft: "-80px",
        marginRight: "-1.25rem",
        width: "calc(100% + 80px + 1.25rem)",
        overflowX: "visible",
        overflowY: "visible",
      }}
    >
      {!isFirst && (
        <div
          className="flex-1 h-[2px] rounded-full opacity-80"
          style={
            {
              WebkitFlex: "1 1 0%",
              flex: "1 1 0%",
              backgroundImage:
                "repeating-linear-gradient(to right, rgb(209, 213, 219) 0px, rgb(209, 213, 219) 8px, transparent 8px, transparent 16px)",
            } as React.CSSProperties
          }
        />
      )}
      <div
        className={`px-4 py-1 border-1 rounded-full shadow-sm z-10 ${isFirst ? "mx-4" : "mx-0"} ${
          isDarkTheme ? "bg-[#071526] border-gray-500" : "bg-white border-gray-300"
        }`}
        style={{
          ...(isFirst ? { marginTop: "-32px" } : {}),
          WebkitTransform: "translateZ(0)",
          transform: "translateZ(0)",
        }}
      >
        <span
          className={`text-sm font-medium ${isDarkTheme ? "dark:text-white" : "text-gray-700"}`}
        >
          {dateGroup}
        </span>
      </div>
      {!isFirst && (
        <div
          className="flex-1 h-[2px] rounded-full opacity-80"
          style={
            {
              WebkitFlex: "1 1 0%",
              flex: "1 1 0%",
              backgroundImage:
                "repeating-linear-gradient(to right, rgb(209, 213, 219) 0px, rgb(209, 213, 219) 8px, transparent 8px, transparent 16px)",
            } as React.CSSProperties
          }
        />
      )}
    </div>

    {/* Activities */}
    <div
      className="space-y-0"
      style={{ width: "100%", overflowX: "visible", overflowY: "visible" }}
    >
      {activities.map((activity) => (
        <ActivityCard
          key={activity.id}
          activity={activity}
          isDarkTheme={isDarkTheme}
          onOpenDoc={onOpenDoc}
        />
      ))}
    </div>
  </div>
);

const ThreadsContainer = ({
  isAdvisorView,
  proposalId,
  entityId,
  isPlan = false,
  threadType = "PENDING",
  isPolicy = false,
  policyId = "",
  isReadOnly = false,
  isEntity = false,
  selectedYear = 1,
}: {
  isAdvisorView?: boolean;
  proposalId: string;
  isPlan?: boolean;
  threadType?: string;
  isPolicy?: boolean;
  policyId?: string;
  isReadOnly?: boolean;
  isEntity?: boolean;
  entityId?: string;
  selectedYear?: any;
}) => {
  const [timelineData, setTimelineData] = useState<TimelineActivity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [currentS3Key, setCurrentS3Key] = useState<string | null>(null);
  const [contentTypeFilter, setContentTypeFilter] = useState<CONTENT_TYPE[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<{
    category: string[] | null;
    relatedPerson: string[] | null;
    dateRange: { from: Date | null; to: Date | null };
  }>({
    category: null,
    relatedPerson: null,
    dateRange: { from: null, to: null },
  });

  const globalTheme = getSessionStore("globalTheme");
  const isDarkTheme = globalTheme === THEME.DARK;
  const userAccess = getObjectFromLocalStore("useraccess");
  const isContact = userAccess?.userType === USERTYPE.CONTACT;
  const isEditorVisibleForReview = threadType === "REVIEW" && isContact;

  const timelineLengthRef = useRef<number>(0);
  useEffect(() => {
    timelineLengthRef.current = timelineData.length;
  }, [timelineData.length]);

  // Ref for intersection observer
  const observerRef = useRef<HTMLDivElement | null>(null);

  // Redux
  const dispatch = useDispatch();
  const { getDocPendingProposals } = useSelector(getAllPendingProposals);
  const advisorSelectedContactId = getSessionStore("contactForPropsalID");

  // Document click handler (downloads file)
  const onOpenDoc = (s3Key: string) => {
    setCurrentS3Key(s3Key);
    dispatch({
      type: GET_DOCUMENT,
      payload: {
        address: "proposalPendingDocLink",
        key: encodeURIComponent(s3Key),
        requestParams: true,
      },
    });
  };

  // Handle document URL when received - download file
  useEffect(() => {
    if (getDocPendingProposals?.data?.data && currentS3Key) {
      const url = getDocPendingProposals.data.data;
      // Download the file instead of opening in new tab
      fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
          const objectUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = objectUrl;
          const fileName = getFileName(currentS3Key) || "download";
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(objectUrl);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error("Failed to download file:", err);
        })
        .finally(() => {
          // Reset the document state
          setCurrentS3Key(null);
          dispatch(resetDocData());
        });
    }
  }, [getDocPendingProposals?.data?.data, currentS3Key, dispatch]);
  const getTimelineData = async (
    page: number = 1,
    append: boolean = false,
    customFilters?: {
      category: string[] | null;
      relatedPerson: string[] | null;
      dateRange: { from: Date | null; to: Date | null };
    },
  ) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      // Use custom filters if provided, otherwise use appliedFilters from state
      const filtersToUse = customFilters || appliedFilters;

      let filterQuery = "";
      if (contentTypeFilter.length > 0) {
        filterQuery += `&contentType=${JSON.stringify(contentTypeFilter)}`;
      }

      if (filtersToUse.category && filtersToUse.category.length > 0) {
        const categoryMapping: { [key: string]: string } = {
          Plans: "PLANS",
          Proposal: "PROPOSAL",
          Policies: "POLICIES",
          Financing: "FINANCING",
          Pending: "PENDING",
        };
        const mapped = filtersToUse.category
          .map((c) => categoryMapping[c])
          .filter((v) => !!v) as string[];

        if (mapped.length > 0) {
          filterQuery += `&threadType=${JSON.stringify(mapped)}`;
        }
      } else if (!isAdvisorView && !isEntity && threadType) {
        filterQuery += `&threadType=${JSON.stringify([threadType])}`;
      }

      if (filtersToUse.relatedPerson && filtersToUse?.relatedPerson?.length > 0) {
        const validPersonValues = ["ADMIN", "ADVISOR", "IAPP", "CONTACT"];
        const mappedPersons = filtersToUse.relatedPerson.filter((p) =>
          validPersonValues.includes(p),
        ) as string[];
        if (mappedPersons.length > 0) {
          filterQuery += `&userPersona=${JSON.stringify(mappedPersons)}`;
        }
      }

      if (filtersToUse.dateRange.from) {
        const year = filtersToUse.dateRange.from.getFullYear();
        const month = String(filtersToUse.dateRange.from.getMonth() + 1).padStart(2, "0");
        const day = String(filtersToUse.dateRange.from.getDate()).padStart(2, "0");
        const fromDate = `${year}-${month}-${day}`;
        filterQuery += `&startDate=${fromDate}`;
      }

      if (filtersToUse.dateRange.to) {
        const year = filtersToUse.dateRange.to.getFullYear();
        const month = String(filtersToUse.dateRange.to.getMonth() + 1).padStart(2, "0");
        const day = String(filtersToUse.dateRange.to.getDate()).padStart(2, "0");
        const toDate = `${year}-${month}-${day}`;
        filterQuery += `&endDate=${toDate}`;
      }

      let response;
      if (isAdvisorView) {
        const advisorContactId = String(advisorSelectedContactId || "").trim();
        const advisorQuery = `pageNumber=${page}&pageSize=100${
          advisorContactId ? `&contactId=${encodeURIComponent(advisorContactId)}` : ""
        }&threadsOnly=true${filterQuery}`;
        response = await makeRequest("advisorContactAllThreads").get(advisorQuery, false, true);
      } else if (isEntity) {
        const id = entityId?.trim();
        if (!id) {
          if (!append) {
            setTimelineData([]);
          }
          setHasMore(false);
          return;
        }
        const pathKey = `${encodeURIComponent(id)}?pageNumber=${page}&pageSize=100${filterQuery}`;
        response = await makeRequest("entityThreads").get(pathKey, true, false);
      } else {
        let query = `${isPlan ? "planId" : "proposalId"}=${proposalId}&page=${page}&pageSize=100${
          isPlan && selectedYear ? `&planYear=${selectedYear}` : ""
        }${isPolicy ? `&policyId=${policyId}` : ""}`;
        query += filterQuery;
        response = await makeRequest("pfthreads").get(query, false, true);
      }

      const responseData = response.data;
      const payload = responseData?.data;
      const pagination = responseData?.pagination ?? payload?.pagination;

      const threads: Thread[] = Array.isArray(payload?.threads)
        ? payload.threads
        : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];

      const totalItems =
        typeof payload?.total === "number"
          ? payload.total
          : typeof pagination?.total === "number"
          ? pagination.total
          : threads.length;

      // Transform API threads to timeline activities
      const activities = threads.map((thread) => transformThreadToActivity(thread));

      if (append) {
        // Append new data to existing data
        setTimelineData((prevData) => [...prevData, ...activities]);
      } else {
        // Replace data (initial load or filter change)
        setTimelineData(activities);
      }

      // Check if there's more data to load
      const totalLoaded = append
        ? timelineLengthRef.current + activities.length
        : activities.length;
      setHasMore(totalLoaded < totalItems);
    } catch (error) {
      if (!append) {
        setTimelineData([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load more data when user scrolls to bottom
  const loadMore = useCallback(() => {
    if (!loadingMore && !loading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      getTimelineData(nextPage, true);
    }
  }, [loadingMore, loading, hasMore, currentPage]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [loadMore]);

  // Initial load and filter changes
  useEffect(() => {
    setCurrentPage(1);
    setTimelineData([]);
    setHasMore(true);
    getTimelineData(1, false);
  }, [contentTypeFilter, appliedFilters, isEntity, entityId, selectedYear]);

  // Handler for when filters are applied
  const handleFiltersApplied = async (filters: {
    category: string[] | null;
    relatedPerson: string[] | null;
    dateRange: { from: Date | null; to: Date | null };
  }) => {
    // Reset pagination
    setCurrentPage(1);
    setTimelineData([]);
    setHasMore(true);
    // Call API with the new filters
    await getTimelineData(1, false, filters);
    // Update the state after API call succeeds
    setAppliedFilters(filters);
  };

  // Handler for when thread is created
  const handleThreadCreated = () => {
    setCurrentPage(1);
    setTimelineData([]);
    setHasMore(true);
    getTimelineData(1, false);
  };
  const handleContentTypeFilterChange = useCallback((nextFilter: CONTENT_TYPE) => {
    setContentTypeFilter((prev) =>
      prev.includes(nextFilter) ? prev.filter((t) => t !== nextFilter) : [...prev, nextFilter],
    );
  }, []);

  // Group activities by date
  const groupedActivities = timelineData.reduce((acc, activity) => {
    if (!acc[activity.dateGroup]) {
      acc[activity.dateGroup] = [];
    }
    acc[activity.dateGroup].push(activity);
    return acc;
  }, {} as Record<string, TimelineActivity[]>);

  return (
    <div
      className="min-h-screen p-2"
      style={{
        overflowX: "visible",
        width: "100%",
        maxWidth: "100vw",
        boxSizing: "border-box",
        position: "relative",
        paddingLeft: "calc(1rem + 50px)",
        paddingRight: "1rem",
      }}
    >
      <div
        className="w-full mx-auto"
        style={{
          maxWidth: "100%",
          overflowX: "visible",
          width: "100%",
          boxSizing: "border-box",
          position: "relative",
        }}
      >
        {!isEditorVisibleForReview && (
          <div className="mb-4" style={{ width: "100%", overflowX: "hidden" }}>
            <PremiumFinanceThreadEditor
              contentTypeFilter={contentTypeFilter}
              setContentTypeFilter={handleContentTypeFilterChange}
              onThreadCreated={handleThreadCreated}
              onFiltersApplied={handleFiltersApplied}
              isDarkTheme={isDarkTheme}
              proposalId={proposalId}
              isReadOnly={isReadOnly}
              isPlan={isPlan}
              threadType={threadType}
              isPolicy={isPolicy}
              policyId={policyId}
              isAdvisorView={isAdvisorView || isEntity}
            />
          </div>
        )}
        {/* Timeline Container */}
        <div
          className="relative rounded-xl"
          style={{
            padding: "1rem",
            paddingLeft: "80px",
            paddingRight: "1.25rem",
            overflowX: "visible",
            overflowY: "visible",
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            position: "relative",
          }}
        >
          {/* SVG dashed border for smooth rounded corners */}
          <svg
            className="pointer-events-none absolute inset-0"
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            style={{
              WebkitTransform: "translateZ(0)",
              transform: "translateZ(0)",
            }}
          >
            <rect
              x="1"
              y="1"
              width="calc(100% - 2px)"
              height="calc(100% - 2px)"
              rx="12"
              ry="12"
              fill="none"
              stroke="rgb(209,213,219)"
              strokeWidth="2"
              strokeDasharray="8,8"
              strokeLinecap="round"
              style={{
                WebkitTransform: "translateZ(0)",
                transform: "translateZ(0)",
              }}
            />
          </svg>
          <div
            className="relative"
            style={{
              width: "100%",
              overflowX: "visible",
              overflowY: "visible",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {/* Loading State */}
            {loading && timelineData.length === 0 && (
              <div className="flex justify-center items-center py-8">
                <i className="pi pi-spin pi-spinner text-2xl text-gray-500" />
              </div>
            )}

            {/* Empty State */}
            {!loading && timelineData.length === 0 && (
              <div className="flex flex-col justify-center items-center py-8 text-gray-500">
                <i className="pi pi-inbox text-4xl mb-2" />
                <p>
                  {isEntity && !entityId?.trim()
                    ? "Select an entity to view threads for this contact."
                    : "No threads found"}
                </p>
              </div>
            )}

            {/* Timeline Content */}
            {timelineData.length > 0 && (
              <div
                className="space-y-0"
                style={{ width: "100%", overflowX: "visible", overflowY: "visible" }}
              >
                {Object.entries(groupedActivities).map(([dateGroup, activities], index) => (
                  <DateGroup
                    isDarkTheme={isDarkTheme}
                    key={dateGroup}
                    dateGroup={dateGroup}
                    activities={activities}
                    isFirst={index === 0}
                    onOpenDoc={onOpenDoc}
                  />
                ))}

                {/* Intersection Observer Target */}
                {hasMore && (
                  <div ref={observerRef} className="flex justify-center items-center py-4">
                    {loadingMore && <i className="pi pi-spin pi-spinner text-xl text-gray-500" />}
                  </div>
                )}

                {/* End of list indicator */}
                {!hasMore && timelineData.length > 0 && (
                  <div
                    className="flex justify-center items-center py-4 text-gray-500 text-sm"
                    style={{ width: "100%", overflowX: "hidden" }}
                  >
                    No more threads to load
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreadsContainer;
